import { assertNever, clsx } from "@regrello/core-utils";
import { useCallback } from "react";
import { RegrelloSpinner } from "../../../feedback/spinner/RegrelloSpinner";
import type { RegrelloIntent } from "../../../tokens/enums/regrelloIntent";
import type { RegrelloJustification } from "../../../tokens/enums/regrelloJustification";
import type { RegrelloShape } from "../../../tokens/enums/regrelloShape";
import { RegrelloSize } from "../../../tokens/enums/regrelloSize";
import { RegrelloIcon, type RegrelloIconName } from "../../../visual/icons/RegrelloIcon";
import { RegrelloIconStyler } from "../../../visual/icons/RegrelloIconStyler";
import type { RegrelloButtonVariant } from "../RegrelloButtonVariant";

export interface RegrelloButtonContentsProps {
  /**
   * Whether this button is in an active or selected state.
   *
   * @default false
   */
  active?: boolean;

  /**
   * Whether to allow text to wrap in the button. This should almost always be `false`, except for
   * when making a custom button.
   *
   * @default false
   */
  allowWrap?: boolean;

  /**
   * Additional class to apply to button content container.
   */
  contentClassName?: string;

  /**
   * Whether the button is non-interactive.
   *
   * @default false
   */
  disabled?: boolean;

  /**
   * An icon to show after the button text.
   */
  endIcon?: RegrelloIconName | React.ReactElement;

  /**
   * If `true`, the button will take up the full width of its container.
   */
  fullWidth?: boolean;

  /**
   * Changes size of the button to look good with icon.
   */
  iconOnly?: boolean;

  /**
   * A semantic intent for the button.
   *
   * @default "neutral"
   *
   * __Warning:__ `warning` and `success` are not yet supported in the Button component.
   */
  intent?: Exclude<RegrelloIntent, "warning" | "success">;

  /**
   * How to lay out the icons and text within the button. Useful when {@see fullWidth} is `true`.
   *
   * @default RegrelloJustification.CENTER
   */
  justification?: RegrelloJustification;

  /**
   * Whether to show the button in a non-interactive loading state.
   */
  loading?: boolean;

  /**
   * The ARIA role of the button. For example, `"radio"` if used within a segmented button group.
   */
  role?: string;

  /**
   * The shape of the button's corners.
   *
   * @default "rectangle"
   */
  shape?: RegrelloShape;

  /**
   * The size of the button.
   *
   * ## Rules
   * Here are some rules for when to use each button size:
   *
   * | Size | Usage |
   * | --- | --- |
   * | `x-large` | Use if and only if the button is particularly important, ideally uniquely important (e.g., the sole button in an empty state) |
   * | `large` _(default)_ | The default size. Use this unless you have a very good reason not to. This drives consistency across the app. |
   * | `medium` | Avoid in general; this is present solely for some internal design-system use cases (e.g., buttons inside of inputs). |
   * | `small` | Avoid in general; this is present solely for some internal design-system use cases (e.g., buttons inside of inputs). |
   * | `x-small` | Use if you really need to fit the button in a tight vertical space. |
   *
   * @default "large"
   */
  size?: RegrelloSize;

  /**
   * An icon to show before the button text.
   */
  startIcon?: RegrelloIconName | React.ReactElement;

  /**
   * The visual emphasis of the button (solid, outlined, ghost).
   *
   * @default "solid"
   */
  variant?: RegrelloButtonVariant;
}

/** The internal display contents of a `RegrelloButton` or `RegrelloAnchorButton`. */
export function RegrelloButtonContents({
  allowWrap = false,
  children,
  contentClassName,
  endIcon,
  iconOnly,
  intent = "neutral",
  loading,
  size = "large",
  startIcon,
  variant = "solid",
}: React.PropsWithChildren<RegrelloButtonContentsProps>) {
  const renderButtonIcon = useCallback(
    (icon: RegrelloIconName | React.ReactElement) => {
      const iconSize = getIconSizeForButtonOfSize(size);
      // Use "block" instead of "inline-block" to eliminate excessive line height in some icons.
      return typeof icon === "string" ? (
        <RegrelloIcon iconName={icon} size={iconSize} />
      ) : (
        <RegrelloIconStyler size={iconSize}>{icon}</RegrelloIconStyler>
      );
    },
    [size],
  );

  return (
    <>
      {/* Loading state */}
      {loading ? (
        <RegrelloSpinner
          className={clsx("absolute", "text-textContrast", {
            "text-textDefault": intent === "neutral" || variant !== "solid",
          })}
        />
      ) : null}

      {/* Start icon */}
      {startIcon != null && (
        <div className={clsx("pointer-events-none", { "mr-1": !iconOnly })}>{renderButtonIcon(startIcon)}</div>
      )}

      {/* Text */}
      {iconOnly ? null : (
        <div
          className={clsx(
            "min-w-0",
            {
              truncate: !allowWrap,
            },
            contentClassName,
          )}
        >
          {children == null || children === "" ? <>&nbsp;</> : children}
        </div>
      )}

      {/* End icon */}
      {!iconOnly && endIcon != null && <div className="pointer-events-none ml-1">{renderButtonIcon(endIcon)}</div>}
    </>
  );
}

function getIconSizeForButtonOfSize(buttonSize: RegrelloSize): RegrelloSize {
  switch (buttonSize) {
    case RegrelloSize.X_SMALL:
      return RegrelloSize.X_SMALL;
    case RegrelloSize.SMALL:
      return RegrelloSize.SMALL;
    case RegrelloSize.MEDIUM:
      return RegrelloSize.SMALL;
    case RegrelloSize.LARGE:
      return RegrelloSize.SMALL;
    case RegrelloSize.X_LARGE:
      return RegrelloSize.MEDIUM;
    default:
      assertNever(buttonSize);
  }
}
